import Papa from 'papaparse';
import { getBlobUrlByConnectionString } from '../../ui/screens/ServiceManagement/auth/blobConfig';

type Environment = 'des' | 'prod';

const PROCESOS_CSV_CONTAINER =
  (process.env.REACT_APP_SM_CREATE_SERVICE_CSV_CONTAINER as string) || 'promptsprocesos';

const PROCESOS_CSV_CONFIG: Record<Environment, { file: string; connectionEnv: string }> = {
  prod: {
    file: 'ListaProcesosServiciosGPT_CEU.csv',
    connectionEnv: 'REACT_APP_SM_SECONDARY_BLOB_CONNECTION_STRING',
  },
  des: {
    file: 'ListaProcesosServiciosGPT_CEU_des.csv',
    connectionEnv: 'REACT_APP_SM_BLOB_CONNECTION_STRING',
  },
};

// Las variables REACT_APP_* se sustituyen en build, por eso se accede a ellas de forma explícita.
const getConnectionString = (environment: Environment): string => {
  const raw =
    environment === 'des'
      ? process.env.REACT_APP_SM_BLOB_CONNECTION_STRING
      : process.env.REACT_APP_SM_SECONDARY_BLOB_CONNECTION_STRING;
  return String(raw || '').trim().replace(/^["']+|["']+$/g, '');
};

export const normalizeProcesoKey = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getCell = (row: Record<string, string>, candidates: string[]): string => {
  const normalizedCandidates = candidates.map(normalizeProcesoKey);
  const key = Object.keys(row).find((header) =>
    normalizedCandidates.includes(normalizeProcesoKey(header))
  );
  return key ? String(row[key] || '').trim() : '';
};

const procesosMapCache: Partial<Record<Environment, Promise<Record<string, string>>>> = {};

const fetchProcesosIdMap = async (environment: Environment): Promise<Record<string, string>> => {
  const { file, connectionEnv } = PROCESOS_CSV_CONFIG[environment];
  const connectionString = getConnectionString(environment);
  if (!connectionString) {
    throw new Error(`Falta ${connectionEnv} para leer ${file}.`);
  }

  const csvUrl = await getBlobUrlByConnectionString(connectionString, PROCESOS_CSV_CONTAINER, file, 'r');
  const response = await fetch(csvUrl);
  if (!response.ok) {
    throw new Error(`Error al leer ${file} (${response.status}).`);
  }

  const csvText = (await response.text()).replace(/^﻿/, '');
  const { data } = Papa.parse<Record<string, string>>(csvText, {
    header: true,
    delimiter: ';',
    skipEmptyLines: true,
  });

  return data.reduce((acc, row) => {
    const nombre = getCell(row, ['NombreProceso', 'Nombre del servicio']);
    const id = getCell(row, ['ID']);
    if (nombre && id) {
      acc[normalizeProcesoKey(nombre)] = id;
    }
    return acc;
  }, {} as Record<string, string>);
};

/** Mapa NombreProceso (normalizado) -> ID, leído del CSV del entorno y cacheado. */
export const getProcesosIdMap = (environment: Environment): Promise<Record<string, string>> => {
  if (!procesosMapCache[environment]) {
    procesosMapCache[environment] = fetchProcesosIdMap(environment).catch((error) => {
      delete procesosMapCache[environment];
      throw error;
    });
  }
  return procesosMapCache[environment]!;
};

/** Devuelve el ID de proceso asociado a un tag, o null si no está en el CSV. */
export const getIdProcesoByTag = async (
  tag: string,
  environment: Environment
): Promise<string | null> => {
  const map = await getProcesosIdMap(environment);
  return map[normalizeProcesoKey(tag)] ?? null;
};
